import { NetworkInterceptor, NetworkRequestConfig, NetworkResponse, NetworkError } from '../interfaces/network.types';

/**
 * TraceInterceptor
 * 
 * Ensures every outgoing request has a unique X-Trace-Id header.
 * This is the cornerstone of our "Observability Hardening" plan, allowing us to 
 * perfectly link frontend actions with backend forensic logs.
 * 
 * @security Trace IDs are for diagnostic correlation only and contain no 
 * PII or sensitive session metadata.
 * 
 * @concurrency Generates a cryptographically strong UUID to prevent collisions 
 * even during high-concurrency batch operations.
 */
export class TraceInterceptor implements NetworkInterceptor {
    /**
     * Intercepts outgoing requests to append the Trace ID.
     * 
     * @param config - The incoming network configuration.
     * @returns Augmented configuration with X-Trace-Id header.
     */
    onRequest(config: NetworkRequestConfig): NetworkRequestConfig {
        // We use the browser's crypto API for enterprise-grade uniqueness
        const traceId = this.generateTraceId();

        return {
            ...config,
            headers: {
                ...config.headers,
                'X-Trace-Id': traceId,
            },
        };
    }

    onResponse(response: NetworkResponse): NetworkResponse {
        return response;
    }

    onError(error: NetworkError): NetworkError {
        return error;
    }

    /**
     * Generates a UUID V4 compliant trace identifier.
     * Uses crypto.randomUUID() if available, otherwise falls back to a math-based 
     * entropy generator for legacy browser support.
     * 
     * @private
     */
    private generateTraceId(): string {
        try {
            if (typeof crypto !== 'undefined' && crypto.randomUUID) {
                return crypto.randomUUID();
            }
        } catch (e) {
            // Fallback for non-secure contexts or older browsers
        }

        // Standard RFC4122 version 4 UUID compliant fallback
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }
}
